import { useMemo } from 'react';
import { useMediaQuery } from '@cotera/client/app/hooks/use-media-query';

const TOUCH_USER_AGENT =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

/**
 * Whether Enter in the composer sends the message, or types a newline.
 *
 * Two different keyboards, and the answer is about which one is under the
 * user's hands — not about how wide the window is.
 *
 * On a phone's on-screen keyboard the Enter key is the only way to start a new
 * line, so sending on it would make multi-line messages impossible; the send
 * button is the send affordance there, which is what every messaging app does.
 * On a physical keyboard the opposite holds: reaching for the mouse to send
 * each message is the slow path, and Enter-to-send (Shift+Enter for a newline)
 * is what people expect.
 *
 * This used to be decided by `useIsMobile()` — a `max-width: 767px` media query
 * — which conflates the two. A desktop browser narrowed past that point (split
 * screen, a side-by-side window, devtools' device mode) still has a physical
 * keyboard, and it lost Enter-to-send with no replacement: Enter typed a
 * newline and the only way to send was to click. Keyboard-only users had no way
 * to send at all without tabbing out of the composer to the button.
 *
 * `(pointer: coarse)` asks the question directly: it describes the *primary*
 * pointing device, so a touchscreen laptop with a trackpad reports `fine` and
 * keeps Enter-to-send, while a phone or tablet reports `coarse`. The user-agent
 * test stays alongside it for browsers that report the query unreliably; either
 * signal is enough to mean "touch keyboard".
 */
export const useEnterSendsMessage = (): boolean => {
  const coarsePointer = useMediaQuery('(pointer: coarse)');
  const touchUserAgent = useMemo(
    () =>
      typeof navigator !== 'undefined' &&
      TOUCH_USER_AGENT.test(navigator.userAgent),
    []
  );
  return !coarsePointer && !touchUserAgent;
};
